import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { slideIndexFromScroll } from './helpers';
import { P, oswald } from './theme';

// -- Product page cover carousel: the cartaz is slide 1, each gallery photo a
//    full 3:4 slide after it. Swipe (native scroll-snap), arrows, keyboard,
//    and the thumbnail strip below all move the same viewport; the active
//    slide is read back from the scroll position so every input agrees. --

const ARROW: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 34,
  height: 34,
  borderRadius: 999,
  border: `1px solid ${P.lightBorder}`,
  background: 'rgba(255,255,255,0.92)',
  color: P.black,
  fontSize: 18,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(45,26,31,.18)',
  zIndex: 4,
};

export function CoverCarousel({
  cover,
  coverThumbUrl,
  coverThumbFallback,
  gallery,
  label,
}: {
  /** The rendered cartaz (slide 1). */
  cover: ReactNode;
  /** Small picture for the cover's thumbnail; falls back to a swatch. */
  coverThumbUrl?: string | null;
  coverThumbFallback: ReactNode;
  gallery: string[];
  label: string;
}) {
  const { t } = useTranslation('p2e');
  const viewportRef = useRef<HTMLDivElement>(null);
  const [index, setIndexState] = useState(0);
  // Mirror for the resize handler, so it never has to re-arm on slide change.
  const indexRef = useRef(0);
  const setIndex = useCallback((i: number) => {
    indexRef.current = i;
    setIndexState(i);
  }, []);
  const total = 1 + gallery.length;

  const readIndex = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    setIndex(slideIndexFromScroll(vp.scrollLeft, vp.clientWidth, total));
  }, [total, setIndex]);

  const goTo = useCallback((i: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const clamped = Math.max(0, Math.min(total - 1, i));
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    vp.scrollTo({ left: clamped * vp.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    setIndex(clamped);
  }, [total, setIndex]);

  // Keep the current slide aligned when the column changes width (sticky
  // column on desktop, full width on phones). Only a real width change
  // re-aligns — the observer's initial call must not interrupt a swipe.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || typeof ResizeObserver === 'undefined') return;
    let lastWidth = vp.clientWidth;
    const ro = new ResizeObserver(() => {
      if (vp.clientWidth === lastWidth) return;
      lastWidth = vp.clientWidth;
      vp.scrollTo({ left: indexRef.current * vp.clientWidth, behavior: 'auto' });
    });
    ro.observe(vp);
    return () => ro.disconnect();
  }, []);

  if (gallery.length === 0) return <>{cover}</>;

  const sheet: React.CSSProperties = {
    aspectRatio: '3 / 4',
    borderRadius: 3,
    overflow: 'hidden',
    background: P.roseMist,
    boxShadow: '0 10px 24px rgba(45,26,31,.16)',
  };

  return (
    <div aria-roledescription="carousel" aria-label={label}>
      <div style={{ position: 'relative' }}>
        <div
          ref={viewportRef}
          className="products-carousel"
          tabIndex={0}
          onScroll={readIndex}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
          }}
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            outline: 'none',
          }}
        >
          <div
            role="group"
            aria-label={t('products.page.gallerySlide', { n: 1, total })}
            style={{ flex: '0 0 100%', scrollSnapAlign: 'start', minWidth: 0 }}
          >
            {cover}
          </div>
          {gallery.map((url, i) => (
            <div
              key={`${url}:${i}`}
              role="group"
              aria-label={t('products.page.gallerySlide', { n: i + 2, total })}
              style={{ flex: '0 0 100%', scrollSnapAlign: 'start', minWidth: 0 }}
            >
              <div className="products-poster" style={sheet}>
                <img
                  src={url}
                  alt=""
                  loading={i === 0 ? 'eager' : 'lazy'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label={t('products.page.galleryPrev')}
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
          style={{ ...ARROW, left: 8, opacity: index === 0 ? 0 : 1, pointerEvents: index === 0 ? 'none' : 'auto' }}
        >
          ‹
        </button>
        <button
          type="button"
          aria-label={t('products.page.galleryNext')}
          disabled={index === total - 1}
          onClick={() => goTo(index + 1)}
          style={{ ...ARROW, right: 8, opacity: index === total - 1 ? 0 : 1, pointerEvents: index === total - 1 ? 'none' : 'auto' }}
        >
          ›
        </button>

        <span
          aria-live="polite"
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            fontFamily: oswald,
            fontSize: 11,
            letterSpacing: '0.12em',
            padding: '2px 8px',
            borderRadius: 999,
            background: 'rgba(45,26,31,0.72)',
            color: P.white,
            zIndex: 4,
            pointerEvents: 'none',
          }}
        >
          {index + 1} / {total}
        </span>
      </div>

      <div
        role="tablist"
        aria-label={label}
        style={{ display: 'flex', gap: 8, marginTop: 12 }}
      >
        {[coverThumbUrl ?? null, ...gallery].map((url, i) => {
          const active = i === index;
          return (
            <button
              key={`${url ?? 'cover'}:${i}`}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={i === 0 ? t('products.page.galleryCover') : t('products.page.gallerySlide', { n: i + 1, total })}
              onClick={() => goTo(i)}
              style={{
                flex: 1,
                aspectRatio: '1 / 1',
                padding: 0,
                borderRadius: 3,
                border: `2px solid ${active ? P.black : P.lightBorder}`,
                background: P.roseMist,
                overflow: 'hidden',
                cursor: 'pointer',
                opacity: active ? 1 : 0.75,
              }}
            >
              {url ? (
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                coverThumbFallback
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
